import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'

import {mapPartialPickup, partialPickupRawSchema} from './partial-pickup-schema'

const jobStatusSchema = z.enum(['planned', 'active', 'completed', 'cancelled'])

export const openWorkJobRawSchema = z.object({
    id: z.string(),
    display_id: z.string(),
    name: z.string(),
    start_date: z.string(),
    date_tbc: z.boolean(),
    status: jobStatusSchema,
    is_pickup: z.boolean(),
})

export const openWorkEquipmentRawSchema = z.object({
    rental_asset_type_name: z.string(),
    quantity: z.number(),
    unit_names: z.array(z.string()),
})

export const customerWorkRawSchema = z.object({
    project_id: z.string(),
    name: z.string(),
    order_number: z.string().nullable(),
    rep_name: z.string().nullable(),
    can_add_job: z.boolean(),
    latest_activity: z.string(),
    is_open: z.boolean(),
    jobs: z.array(openWorkJobRawSchema),
    site_names: z.array(z.string()),
    saved_location_ids: z.array(z.string()),
    equipment_out: z.array(openWorkEquipmentRawSchema),
    partial_pickup: partialPickupRawSchema.nullable(),
})

export const customerWorkSchema = customerWorkRawSchema.transform((raw) => ({
    projectId: raw.project_id,
    name: raw.name,
    orderNumber: raw.order_number,
    repName: raw.rep_name,
    canAddJob: raw.can_add_job,
    latestActivity: raw.latest_activity,
    isOpen: raw.is_open,
    jobs: raw.jobs.map((job) => ({
        id: job.id,
        displayId: job.display_id,
        name: job.name,
        startDate: job.start_date,
        dateTbc: job.date_tbc,
        status: job.status,
        isPickup: job.is_pickup,
    })),
    siteNames: raw.site_names,
    savedLocationIds: raw.saved_location_ids,
    equipmentOut: raw.equipment_out.map((group) => ({
        rentalAssetTypeName: group.rental_asset_type_name,
        quantity: group.quantity,
        unitNames: group.unit_names,
    })),
    partialPickup: mapPartialPickup(raw.partial_pickup),
}))

export type CustomerWorkRaw = z.infer<typeof customerWorkRawSchema>
export type CustomerWork = z.infer<typeof customerWorkSchema>

export const pickupUnitRawSchema = z.object({
    rental_asset_id: z.string(),
    display_name: z.string(),
    unit_name: z.string().nullable(),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    is_stock: z.boolean(),
    location_label: z.string(),
    third_party_rental_asset_offering_id: z.string().nullable(),
    third_party_rental_company_name: z.string().nullable(),
})

export const pickupUnitSchema = pickupUnitRawSchema.transform((raw) => ({
    rentalAssetId: raw.rental_asset_id,
    displayName: raw.display_name,
    /** null when the asset has no unit name. */
    unitName: raw.unit_name,
    rentalAssetTypeId: raw.rental_asset_type_id,
    rentalAssetTypeName: raw.rental_asset_type_name,
    isStock: raw.is_stock,
    locationLabel: raw.location_label,
    thirdPartyRentalAssetOfferingId: raw.third_party_rental_asset_offering_id,
    thirdPartyRentalCompanyName: raw.third_party_rental_company_name,
}))

export type PickupUnitRaw = z.infer<typeof pickupUnitRawSchema>
export type PickupUnit = z.infer<typeof pickupUnitSchema>

export const createPickupJobInputSchema = z.object({
    fromJobId: z.string().nullable(),
    startDate: z.string().nullable(),
    rentalAssetIds: z.array(z.string()),
    stock: z.array(
        z.object({
            rentalAssetTypeId: z.string(),
            thirdPartyRentalAssetOfferingId: z.string().nullable(),
            quantity: z.number(),
        }),
    ),
})

export type CreatePickupJobInput = z.infer<typeof createPickupJobInputSchema>

export const createPickupJobRequestSchema = createPickupJobInputSchema.transform((input) => ({
    from_job_id: input.fromJobId,
    start_date: input.startDate,
    rental_asset_ids: input.rentalAssetIds,
    stock: input.stock.map((line) => ({
        rental_asset_type_id: line.rentalAssetTypeId,
        ...(line.thirdPartyRentalAssetOfferingId
            ? {third_party_rental_asset_offering_id: line.thirdPartyRentalAssetOfferingId}
            : {}),
        quantity: line.quantity,
    })),
}))

export const confirmPickupReturnsInputSchema = z.object({
    yardId: z.string(),
    returnedOn: z.string(),
    rentalAssetIds: z.array(z.string()),
})

export type ConfirmPickupReturnsInput = z.infer<typeof confirmPickupReturnsInputSchema>

export const confirmPickupReturnsRequestSchema = confirmPickupReturnsInputSchema.transform(
    (input) => ({
        yard_id: input.yardId,
        returned_on: input.returnedOn,
        rental_asset_ids: input.rentalAssetIds,
    }),
)

export const pickupReturnResultSchema = z
    .object({returned_count: z.number(), still_out_count: z.number()})
    .transform((raw) => ({returnedCount: raw.returned_count, stillOutCount: raw.still_out_count}))

const pickupJobSummaryRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    display_id: z.string(),
})

export const createdPickupJobSchema = pickupJobSummaryRawSchema
    .extend({other_pickup_jobs: z.array(pickupJobSummaryRawSchema).optional()})
    .transform((raw) => ({
        id: raw.id,
        name: raw.name,
        displayId: raw.display_id,
        /** One more pickup per further site when the units are at several sites. */
        otherPickupJobs: (raw.other_pickup_jobs ?? []).map((job) => ({
            id: job.id,
            name: job.name,
            displayId: job.display_id,
        })),
    }))

export const pickupSoonerInputSchema = z.object({
    startDate: z.string().nullable(),
    // null targets the project's single open pickup.
    pickupJobId: z.string().nullable(),
    fromJobId: z.string().nullable(),
})

export type PickupSoonerInput = z.infer<typeof pickupSoonerInputSchema>

export const pickupSoonerRequestSchema = pickupSoonerInputSchema.transform((input) => ({
    start_date: input.startDate,
    pickup_job_id: input.pickupJobId,
    from_job_id: input.fromJobId,
}))

export const pickupSoonerResultRawSchema = z.object({
    pickup_job_id: z.string(),
    created: z.boolean(),
    units_not_on_pickup: z.number(),
    dispatch_emailed: z.array(z.string()),
    unchanged: z.boolean(),
})

export type PickupSoonerResultRaw = z.infer<typeof pickupSoonerResultRawSchema>

export const pickupSoonerResultSchema = pickupSoonerResultRawSchema.transform((raw) => ({
    pickupJobId: raw.pickup_job_id,
    created: raw.created,
    unitsNotOnPickup: raw.units_not_on_pickup,
    dispatchEmailed: raw.dispatch_emailed,
    unchanged: raw.unchanged,
}))

export const pickupConfirmationSchema = z
    .object({pickup_confirmed_at: z.string(), pickup_confirmed_by_name: z.string()})
    .transform((raw) => ({
        pickupConfirmedAt: tzDateFromIso.parse(raw.pickup_confirmed_at),
        pickupConfirmedByName: raw.pickup_confirmed_by_name,
    }))

export const pickupToConfirmRawSchema = z.object({
    job_id: z.string(),
    project_id: z.string(),
    display_id: z.string(),
    name: z.string(),
    start_date: z.string(),
    customer_name: z.string(),
    rep_name: z.string().nullable(),
    can_confirm: z.boolean(),
})

export const pickupsToConfirmRawSchema = z.object({
    day: z.string(),
    pickups: z.array(pickupToConfirmRawSchema),
})

export type PickupsToConfirmRaw = z.infer<typeof pickupsToConfirmRawSchema>

export const pickupsToConfirmSchema = pickupsToConfirmRawSchema.transform((raw) => ({
    day: raw.day,
    pickups: raw.pickups.map((pickup) => ({
        jobId: pickup.job_id,
        projectId: pickup.project_id,
        displayId: pickup.display_id,
        name: pickup.name,
        startDate: pickup.start_date,
        customerName: pickup.customer_name,
        repName: pickup.rep_name,
        canConfirm: pickup.can_confirm,
    })),
}))

export type PickupsToConfirm = z.infer<typeof pickupsToConfirmSchema>
export type PickupToConfirm = PickupsToConfirm['pickups'][number]
